import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faAngleLeft,
  faAngleRight,
  faArrowLeft,
  faDownload,
  faRefresh,
  faSearch,
  faTimes,
  faUser,
  faWandMagicSparkles,
  faRobot,
  faXmark,
} from "@fortawesome/free-solid-svg-icons";
import {
  getDepositSlipBatchUploadHistory,
  getDepositSlipBatchProcessedRows,
  exportDepositSlipBatchProcessedRows,
  getDepositSlipBatchAiSummary,
} from "../../services/settlementApi";

const DepositSlipBatchUploadProcessedPage = () => {
  const navigate = useNavigate();
  const { uploadId } = useParams();

  const [uploadInfo, setUploadInfo] = useState(null);
  const [allRows, setAllRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [rowType, setRowType] = useState("all");
  const [exporting, setExporting] = useState(false);
  const [aiModalOpen, setAiModalOpen] = useState(false);
  const [aiSummary, setAiSummary] = useState(null);
  const [aiLoading, setAiLoading] = useState(false);
  const [aiError, setAiError] = useState("");

  const [filterPaymentRef, setFilterPaymentRef] = useState("");
  const [filterRefNo, setFilterRefNo] = useState("");
  const [filterReceivedDate, setFilterReceivedDate] = useState("");
  const [filterReceivedBy, setFilterReceivedBy] = useState("");
  const [filterSourceStatus, setFilterSourceStatus] = useState("");
  const [filterResultStatus, setFilterResultStatus] = useState("");
  const [filterDetails, setFilterDetails] = useState("");

  const [page, setPage] = useState(1);
  const [perPage] = useState(50);

  useEffect(() => {
    const fetchUploadInfo = async () => {
      try {
        const response = await getDepositSlipBatchUploadHistory({ page: 1, limit: 1000 });
        const upload = response.data.data?.find((u) => u.upload_id === uploadId);
        if (upload) {
          setUploadInfo(upload);
        } else {
          toast.error("Upload not found");
          navigate("/deposit-slip-batch-upload-history");
        }
      } catch (error) {
        toast.error("Failed to fetch upload info");
        console.error(error);
      }
    };

    if (uploadId) fetchUploadInfo();
  }, [uploadId, navigate]);

  const fetchRows = useCallback(async () => {
    if (!uploadId) return;

    setLoading(true);
    try {
      const response = await getDepositSlipBatchProcessedRows(uploadId, {
        page: 1,
        limit: 5000,
        type: rowType,
      });
      const data = response.data.data || [];
      setAllRows(data);
      setPage(1);
    } catch (error) {
      toast.error("Failed to fetch processed rows");
      console.error(error);
    } finally {
      setLoading(false);
    }
  }, [uploadId, rowType]);

  useEffect(() => {
    fetchRows();
  }, [fetchRows]);

  const formatDate = (d) => {
    if (!d) return "—";

    const formatLongDate = (year, month, day) => {
      const y = Number(year);
      const m = Number(month);
      const dd = Number(day);

      if (!Number.isInteger(y) || !Number.isInteger(m) || !Number.isInteger(dd) || m < 1 || m > 12 || dd < 1 || dd > 31) {
        return null;
      }

      const test = new Date(Date.UTC(y, m - 1, dd));
      if (test.getUTCFullYear() !== y || test.getUTCMonth() !== m - 1 || test.getUTCDate() !== dd) {
        return null;
      }

      return test.toLocaleDateString("en-US", {
        year: "numeric",
        month: "long",
        day: "numeric",
        timeZone: "UTC",
      });
    };

    if (typeof d === "string") {
      const dmy = d.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
      if (dmy) {
        const converted = formatLongDate(dmy[3], dmy[2], dmy[1]);
        if (converted) return converted;
      }

      const ymd = d.match(/^(\d{4})-(\d{2})-(\d{2})(?:\s.*)?$/);
      if (ymd) {
        const converted = formatLongDate(ymd[1], ymd[2], ymd[3]);
        if (converted) return converted;
      }
    }

    const parsed = new Date(d);
    if (!Number.isNaN(parsed.getTime())) {
      return parsed.toLocaleDateString("en-US", {
        year: "numeric",
        month: "long",
        day: "numeric",
      });
    }

    return d;
  };

  const formatDateTime = (d) => {
    if (!d) return "—";
    return new Date(d).toLocaleString("en-PH", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const filteredRows = useMemo(() => {
    const match = (val, filter) => !filter || String(val ?? "").toLowerCase().includes(filter.toLowerCase());

    return allRows.filter((row) => {
      const receivedDateSearch = `${row.received_date ?? ""} ${formatDate(row.received_date)}`;

      if (!match(row.payment_ref_no, filterPaymentRef)) return false;
      if (!match(row.ref_no, filterRefNo)) return false;
      if (!match(receivedDateSearch, filterReceivedDate)) return false;
      if (!match(row.received_by, filterReceivedBy)) return false;
      if (!match(row.status, filterSourceStatus)) return false;
      if (!match(row.result_type, filterResultStatus)) return false;

      const detailsText = row.error_message || row.processed_by || "";
      if (!match(detailsText, filterDetails)) return false;

      return true;
    });
  }, [
    allRows,
    filterPaymentRef,
    filterRefNo,
    filterReceivedDate,
    filterReceivedBy,
    filterSourceStatus,
    filterResultStatus,
    filterDetails,
  ]);

  const paginatedRows = useMemo(() => {
    const start = (page - 1) * perPage;
    return filteredRows.slice(start, start + perPage);
  }, [filteredRows, page, perPage]);

  const totalPages = Math.max(1, Math.ceil(filteredRows.length / perPage));

  const hasActiveFilters =
    filterPaymentRef ||
    filterRefNo ||
    filterReceivedDate ||
    filterReceivedBy ||
    filterSourceStatus ||
    filterResultStatus ||
    filterDetails;

  const handleClearFilters = () => {
    setFilterPaymentRef("");
    setFilterRefNo("");
    setFilterReceivedDate("");
    setFilterReceivedBy("");
    setFilterSourceStatus("");
    setFilterResultStatus("");
    setFilterDetails("");
    setPage(1);
  };

  const handleExport = async (exportType) => {
    if (!uploadId) return;

    setExporting(true);
    try {
      const response = await exportDepositSlipBatchProcessedRows(uploadId, { type: exportType });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `deposit_slip_batch_${uploadId}_${exportType}_${Date.now()}.csv`);
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
      window.URL.revokeObjectURL(url);
      toast.success(`Exported ${exportType} rows successfully`);
    } catch (error) {
      toast.error("Export failed");
      console.error(error);
    } finally {
      setExporting(false);
    }
  };

  const handleOpenAiSummary = async () => {
    setAiModalOpen(true);
    setAiError("");

    if (aiSummary) return;

    setAiLoading(true);
    try {
      const response = await getDepositSlipBatchAiSummary(uploadId);
      setAiSummary(response.data.data);
    } catch (error) {
      const msg = error.response?.data?.message || "Failed to generate AI summary";
      setAiError(msg);
      toast.error(msg);
    } finally {
      setAiLoading(false);
    }
  };

  const getResultBadge = (resultType) => {
    if (resultType === "success") {
      return <span className="px-2 py-1 rounded text-xs font-semibold bg-green-100 text-green-800">Success</span>;
    }

    return <span className="px-2 py-1 rounded text-xs font-semibold bg-red-100 text-red-800">Failed</span>;
  };

  if (!uploadInfo) {
    return (
      <div className="p-6 bg-gray-50 min-h-screen">
        <div className="text-center py-12">
          <p className="text-gray-600">Loading upload information...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 bg-gray-50 min-h-screen">
      <div className="max-w-full mx-auto">
        <div className="mb-6 flex items-center gap-4">
          <button
            onClick={() => navigate("/deposit-slip-batch-upload-history")}
            className="px-4 py-2 bg-gray-200 hover:bg-gray-300 text-gray-800 rounded-lg flex items-center gap-2 text-sm font-medium"
          >
            <FontAwesomeIcon icon={faArrowLeft} /> Back
          </button>
          <div>
            <h1 className="text-2xl font-bold text-gray-800">Deposit Slip CSV Processed</h1>
            <p className="text-gray-600 text-sm mt-0.5">{uploadInfo.original_file_name}</p>
          </div>
          <button
            onClick={handleOpenAiSummary}
            disabled={uploadInfo.status === "processing" || uploadInfo.status === "uploaded"}
            className="group relative ml-auto px-5 py-2.5 rounded-xl font-semibold text-sm text-white
              bg-gradient-to-r from-purple-600 via-indigo-600 to-blue-600
              hover:from-purple-500 hover:via-indigo-500 hover:to-blue-500
              disabled:from-gray-400 disabled:via-gray-400 disabled:to-gray-400 disabled:cursor-not-allowed
              shadow-lg shadow-indigo-500/25 hover:shadow-indigo-500/40
              transition-all duration-300 ease-out hover:scale-[1.03] active:scale-[0.98]
              flex items-center gap-2.5 overflow-hidden"
          >
            <span className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent
              translate-x-[-200%] group-hover:translate-x-[200%] transition-transform duration-1000 ease-in-out" />
            <span className="absolute inset-0 rounded-xl opacity-0 group-hover:opacity-100
              transition-opacity duration-500 ring-2 ring-indigo-400/50 ring-offset-2 ring-offset-transparent" />
            <FontAwesomeIcon
              icon={faWandMagicSparkles}
              className="relative z-10 text-base group-hover:rotate-12 transition-transform duration-300"
            />
            <span className="relative z-10">AI Summary</span>
            <span className="relative z-10 flex h-2 w-2">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-yellow-300 opacity-75" />
              <span className="relative inline-flex rounded-full h-2 w-2 bg-yellow-400" />
            </span>
          </button>
        </div>

        <div className="bg-white rounded-lg shadow-md p-5 mb-5">
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
            <div>
              <p className="text-xs text-gray-500 mb-1">Total Rows</p>
              <p className="text-2xl font-bold text-gray-800">{uploadInfo.total_rows || 0}</p>
            </div>
            <div>
              <p className="text-xs text-gray-500 mb-1">Success</p>
              <p className="text-2xl font-bold text-green-600">{uploadInfo.success_count || 0}</p>
            </div>
            <div>
              <p className="text-xs text-gray-500 mb-1">Failed</p>
              <p className="text-2xl font-bold text-red-600">{uploadInfo.failed_count || 0}</p>
            </div>
            <div>
              <p className="text-xs text-gray-500 mb-1">Status</p>
              <p
                className={`text-lg font-bold ${
                  uploadInfo.status === "completed"
                    ? "text-green-600"
                    : uploadInfo.status === "failed"
                    ? "text-red-600"
                    : "text-yellow-600"
                }`}
              >
                {(uploadInfo.status || "").charAt(0).toUpperCase() + (uploadInfo.status || "").slice(1)}
              </p>
            </div>
            <div>
              <p className="text-xs text-gray-500 mb-1">Uploaded By</p>
              <p className="text-sm font-semibold text-gray-700 flex items-center gap-1">
                <FontAwesomeIcon icon={faUser} className="text-gray-400" />
                {uploadInfo.uploaded_by_name || "—"}
              </p>
            </div>
            <div>
              <p className="text-xs text-gray-500 mb-1">Uploaded</p>
              <p className="text-xs text-gray-700">{formatDateTime(uploadInfo.created_at)}</p>
            </div>
          </div>

          {uploadInfo.error_message && (
            <div className="mt-3 p-3 bg-red-50 border border-red-200 rounded">
              <p className="text-sm text-red-800">
                <strong>Error:</strong> {uploadInfo.error_message}
              </p>
            </div>
          )}
        </div>

        <div className="bg-white rounded-lg shadow-sm p-4 mb-4">
          <div className="flex flex-col lg:flex-row gap-4 items-start lg:items-center justify-between">
            <div className="flex gap-2 flex-wrap">
              {[
                { key: "all", label: `All Rows (${uploadInfo.total_rows || 0})` },
                { key: "success", label: `Success (${uploadInfo.success_count || 0})` },
                { key: "failed", label: `Failed (${uploadInfo.failed_count || 0})` },
              ].map(({ key, label }) => (
                <button
                  key={key}
                  onClick={() => {
                    setRowType(key);
                    setPage(1);
                  }}
                  className={`px-3 py-2 rounded text-sm font-medium ${
                    rowType === key
                      ? key === "all"
                        ? "bg-blue-600 text-white"
                        : key === "success"
                        ? "bg-green-600 text-white"
                        : "bg-red-600 text-white"
                      : "bg-gray-200 text-gray-700 hover:bg-gray-300"
                  }`}
                >
                  {label}
                </button>
              ))}
              <button
                onClick={fetchRows}
                disabled={loading}
                className="px-3 py-2 bg-gray-200 hover:bg-gray-300 disabled:bg-gray-200 text-gray-700 rounded flex items-center gap-2 text-sm"
              >
                <FontAwesomeIcon icon={faRefresh} className={loading ? "animate-spin" : ""} /> Refresh
              </button>
            </div>

            <div className="flex gap-2 flex-wrap">
              <button
                onClick={() => handleExport("all")}
                disabled={exporting || (uploadInfo.total_rows || 0) === 0}
                className="px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 text-white rounded-lg text-sm flex items-center gap-2"
              >
                <FontAwesomeIcon icon={faDownload} /> Export All
              </button>
              <button
                onClick={() => handleExport("failed")}
                disabled={exporting || (uploadInfo.failed_count || 0) === 0}
                className="px-4 py-2 bg-red-600 hover:bg-red-700 disabled:bg-gray-400 text-white rounded-lg text-sm flex items-center gap-2"
              >
                <FontAwesomeIcon icon={faDownload} /> Export Failed
              </button>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm p-4 mb-4">
          <div className="flex items-center justify-between mb-3">
            <p className="text-sm font-semibold text-gray-700 flex items-center gap-2">
              <FontAwesomeIcon icon={faSearch} className="text-gray-500" />
              Filter Rows (frontend)
            </p>
            {hasActiveFilters && (
              <button
                onClick={handleClearFilters}
                className="text-xs text-red-600 hover:text-red-800 flex items-center gap-1"
              >
                <FontAwesomeIcon icon={faTimes} /> Clear Filters
              </button>
            )}
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-7 gap-3">
            <div>
              <label className="block text-xs text-gray-500 mb-1">Payment Ref No</label>
              <input
                type="text"
                value={filterPaymentRef}
                onChange={(e) => {
                  setFilterPaymentRef(e.target.value);
                  setPage(1);
                }}
                placeholder="Search..."
                className="w-full px-2.5 py-1.5 border border-gray-300 rounded text-xs focus:outline-none focus:border-blue-500"
              />
            </div>
            <div>
              <label className="block text-xs text-gray-500 mb-1">Ref No</label>
              <input
                type="text"
                value={filterRefNo}
                onChange={(e) => {
                  setFilterRefNo(e.target.value);
                  setPage(1);
                }}
                placeholder="Search..."
                className="w-full px-2.5 py-1.5 border border-gray-300 rounded text-xs focus:outline-none focus:border-blue-500"
              />
            </div>
            <div>
              <label className="block text-xs text-gray-500 mb-1">Received Date</label>
              <input
                type="text"
                value={filterReceivedDate}
                onChange={(e) => {
                  setFilterReceivedDate(e.target.value);
                  setPage(1);
                }}
                placeholder="MM/DD/YYYY..."
                className="w-full px-2.5 py-1.5 border border-gray-300 rounded text-xs focus:outline-none focus:border-blue-500"
              />
            </div>
            <div>
              <label className="block text-xs text-gray-500 mb-1">Received By</label>
              <input
                type="text"
                value={filterReceivedBy}
                onChange={(e) => {
                  setFilterReceivedBy(e.target.value);
                  setPage(1);
                }}
                placeholder="Search..."
                className="w-full px-2.5 py-1.5 border border-gray-300 rounded text-xs focus:outline-none focus:border-blue-500"
              />
            </div>
            <div>
              <label className="block text-xs text-gray-500 mb-1">Source Status</label>
              <input
                type="text"
                value={filterSourceStatus}
                onChange={(e) => {
                  setFilterSourceStatus(e.target.value);
                  setPage(1);
                }}
                placeholder="Status..."
                className="w-full px-2.5 py-1.5 border border-gray-300 rounded text-xs focus:outline-none focus:border-blue-500"
              />
            </div>
            <div>
              <label className="block text-xs text-gray-500 mb-1">Result</label>
              <select
                value={filterResultStatus}
                onChange={(e) => {
                  setFilterResultStatus(e.target.value);
                  setPage(1);
                }}
                className="w-full px-2.5 py-1.5 border border-gray-300 rounded text-xs focus:outline-none focus:border-blue-500"
              >
                <option value="">All</option>
                <option value="success">Success</option>
                <option value="failed">Failed</option>
              </select>
            </div>
            <div>
              <label className="block text-xs text-gray-500 mb-1">Details</label>
              <input
                type="text"
                value={filterDetails}
                onChange={(e) => {
                  setFilterDetails(e.target.value);
                  setPage(1);
                }}
                placeholder="Error or processor..."
                className="w-full px-2.5 py-1.5 border border-gray-300 rounded text-xs focus:outline-none focus:border-blue-500"
              />
            </div>
          </div>

          {hasActiveFilters && (
            <p className="text-xs text-blue-600 mt-2">
              Showing {filteredRows.length} of {allRows.length} rows (filtered)
            </p>
          )}
        </div>

        <div className="bg-white rounded-lg shadow-md overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-xs lg:text-sm">
              <thead className="bg-gray-100 border-b border-gray-300 sticky top-0">
                <tr>
                  <th className="px-3 py-3 text-left font-semibold text-gray-700">Row #</th>
                  <th className="px-3 py-3 text-left font-semibold text-gray-700">Payment Ref No</th>
                  <th className="px-3 py-3 text-left font-semibold text-gray-700">Ref No</th>
                  <th className="px-3 py-3 text-left font-semibold text-gray-700">Received Date</th>
                  <th className="px-3 py-3 text-left font-semibold text-gray-700">Received By</th>
                  <th className="px-3 py-3 text-left font-semibold text-gray-700">Source Status</th>
                  <th className="px-3 py-3 text-left font-semibold text-gray-700">Result</th>
                  <th className="px-3 py-3 text-left font-semibold text-gray-700">Details</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan="8" className="px-3 py-8 text-center text-gray-500">Loading...</td>
                  </tr>
                ) : paginatedRows.length === 0 ? (
                  <tr>
                    <td colSpan="8" className="px-3 py-8 text-center text-gray-500">No rows found</td>
                  </tr>
                ) : (
                  paginatedRows.map((row, idx) => (
                    <tr key={`${row.id}-${idx}`} className="border-b border-gray-200 hover:bg-gray-50">
                      <td className="px-3 py-2.5 text-gray-700 font-medium">{row.row_number}</td>
                      <td className="px-3 py-2.5 text-gray-800">{row.payment_ref_no || "—"}</td>
                      <td className="px-3 py-2.5 text-gray-800">{row.ref_no || "—"}</td>
                      <td className="px-3 py-2.5 text-gray-800">{formatDate(row.received_date)}</td>
                      <td className="px-3 py-2.5 text-gray-800">{row.received_by || "—"}</td>
                      <td className="px-3 py-2.5 text-gray-800">{row.status || "—"}</td>
                      <td className="px-3 py-2.5">{getResultBadge(row.result_type)}</td>
                      <td className="px-3 py-2.5 text-xs max-w-xs">
                        {row.error_message ? (
                          <div className="text-red-700 truncate" title={row.error_message}>
                            {row.error_message}
                          </div>
                        ) : (
                          <span className="text-gray-500">Processed by {row.processed_by || "—"}</span>
                        )}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          <div className="bg-gray-50 border-t border-gray-200 px-6 py-3 flex items-center justify-between">
            <div className="text-sm text-gray-600">
              Showing {filteredRows.length === 0 ? 0 : (page - 1) * perPage + 1} to{" "}
              {Math.min(page * perPage, filteredRows.length)} of {filteredRows.length} rows
            </div>
            <div className="flex gap-2 items-center">
              <button
                onClick={() => setPage(Math.max(1, page - 1))}
                disabled={page === 1}
                className="px-3 py-1 border border-gray-300 rounded hover:bg-gray-100 disabled:bg-gray-100 disabled:cursor-not-allowed"
              >
                <FontAwesomeIcon icon={faAngleLeft} />
              </button>
              <span className="text-sm font-medium text-gray-700">
                Page {page} of {totalPages}
              </span>
              <button
                onClick={() => setPage(Math.min(totalPages, page + 1))}
                disabled={page >= totalPages}
                className="px-3 py-1 border border-gray-300 rounded hover:bg-gray-100 disabled:bg-gray-100 disabled:cursor-not-allowed"
              >
                <FontAwesomeIcon icon={faAngleRight} />
              </button>
            </div>
          </div>
        </div>
      </div>

      <ToastContainer position="top-right" autoClose={3000} />

      {aiModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div
            className="absolute inset-0 bg-black/50 backdrop-blur-sm animate-fade-in"
            onClick={() => setAiModalOpen(false)}
          />
          <div
            className="relative w-full max-w-3xl max-h-[85vh] bg-white rounded-2xl shadow-2xl
            animate-slide-up flex flex-col overflow-hidden border border-gray-200"
          >
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 bg-gradient-to-r from-purple-600 via-indigo-600 to-blue-600">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-white/20 rounded-lg backdrop-blur-sm">
                  <FontAwesomeIcon icon={faRobot} className="text-white text-lg" />
                </div>
                <div>
                  <h2 className="text-lg font-bold text-white">Deposit Slip Upload AI Summary</h2>
                  <p className="text-xs text-indigo-200">
                    {aiSummary?.model_used ? `Model: ${aiSummary.model_used}` : "Powered by AI"}
                  </p>
                </div>
              </div>
              <button
                onClick={() => setAiModalOpen(false)}
                className="p-2 rounded-lg hover:bg-white/20 text-white transition-colors duration-200"
              >
                <FontAwesomeIcon icon={faXmark} className="text-lg" />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto px-6 py-5">
              {aiLoading && (
                <div className="flex flex-col items-center justify-center py-16 gap-4">
                  <div className="relative">
                    <div className="w-16 h-16 rounded-full border-4 border-indigo-100 border-t-indigo-600 animate-spin" />
                    <FontAwesomeIcon
                      icon={faWandMagicSparkles}
                      className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 text-indigo-600 text-xl animate-pulse"
                    />
                  </div>
                  <div className="text-center">
                    <p className="text-sm font-semibold text-gray-700">Generating AI Summary...</p>
                    <p className="text-xs text-gray-500 mt-1">Analyzing upload data with AI. This may take a few seconds.</p>
                  </div>
                </div>
              )}

              {aiError && !aiLoading && (
                <div className="flex flex-col items-center justify-center py-12 gap-3">
                  <div className="w-14 h-14 bg-red-100 rounded-full flex items-center justify-center">
                    <FontAwesomeIcon icon={faTimes} className="text-red-500 text-xl" />
                  </div>
                  <p className="text-sm text-red-600 font-medium">{aiError}</p>
                  <button
                    onClick={handleOpenAiSummary}
                    className="px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white text-sm rounded-lg transition-colors duration-200"
                  >
                    Try Again
                  </button>
                </div>
              )}

              {aiSummary && !aiLoading && (
                <div
                  className="prose prose-sm max-w-none prose-headings:text-gray-800 prose-p:text-gray-700 prose-ul:text-gray-700 prose-li:text-gray-700"
                  dangerouslySetInnerHTML={{
                    __html:
                      aiSummary.summary_text
                        ?.replace(/^### (.*$)/gim, "<h3>$1</h3>")
                        .replace(/^## (.*$)/gim, "<h2>$1</h2>")
                        .replace(/^# (.*$)/gim, "<h1>$1</h1>")
                        .replace(/\*\*(.*?)\*\*/gim, "<strong>$1</strong>")
                        .replace(/\*(.*?)\*/gim, "<em>$1</em>")
                        .replace(/^- (.*$)/gim, "<li>$1</li>")
                        .replace(/(<li>.*<\/li>)/s, "<ul>$1</ul>")
                        .replace(/\n{2,}/g, "</p><p>")
                        .replace(/\n/g, "<br/>") || "",
                  }}
                />
              )}
            </div>

            {aiSummary && !aiLoading && (
              <div className="px-6 py-3 border-t border-gray-200 bg-gray-50 flex items-center justify-between">
                <div className="text-xs text-gray-500 flex gap-4">
                  {aiSummary.prompt_tokens != null && (
                    <span>Prompt tokens: <strong>{aiSummary.prompt_tokens?.toLocaleString()}</strong></span>
                  )}
                  {aiSummary.completion_tokens != null && (
                    <span>Completion tokens: <strong>{aiSummary.completion_tokens?.toLocaleString()}</strong></span>
                  )}
                  {aiSummary.total_tokens != null && (
                    <span>Total tokens: <strong>{aiSummary.total_tokens?.toLocaleString()}</strong></span>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setAiModalOpen(false)}
                    className="px-4 py-2 bg-gray-200 hover:bg-gray-300 text-gray-700 text-sm rounded-lg transition-colors duration-200 font-medium"
                  >
                    Close
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default DepositSlipBatchUploadProcessedPage;

